import React, { useState, useEffect, useCallback } from 'react';
import { productService } from '../services/api';
import ProductCard from './ProductCard';
import { FiSearch, FiChevronLeft, FiChevronRight } from 'react-icons/fi';

function ProductList({ onAddToCart }) {
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [categories, setCategories] = useState([]);
  const [sortBy, setSortBy] = useState('default');

  // États pour la pagination
  const [nextPage, setNextPage] = useState(null);
  const [prevPage, setPrevPage] = useState(null);

  // Fonction de chargement initiale
  const fetchProducts = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await productService.getAllProducts();
      
      const dataResults = response.results ? response.results : response;
      setNextPage(response.next || null);
      setPrevPage(response.previous || null);

      if (Array.isArray(dataResults)) {
        setProducts(dataResults);
        const uniqueCategories = ['all', ...new Set(dataResults.map(p => 
          p.category_info?.name || p.category || 'Optique'
        ))];
        setCategories(uniqueCategories);
        setFilteredProducts(dataResults);
      }
    } catch (error) {
      console.error("Erreur API:", error);
      setError('Impossible de charger les produits.');
    } finally {
      setLoading(false);
    }
  };

  // FONCTION CORRIGÉE : Gère le clic sur Suivant/Précédent
  const handlePageChange = async (url) => {
    if (!url) return;
    try {
      setLoading(true);
      // On fetch l'URL de pagination fournie par Django
      const res = await fetch(url);
      const data = await res.json();
      
      const results = data.results ? data.results : data;
      setNextPage(data.next || null);
      setPrevPage(data.previous || null);

      if (Array.isArray(results)) {
        setProducts(results);
        setFilteredProducts(results);
        // Retour en haut de page pour l'utilisateur
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    } catch (err) {
      console.error("Erreur pagination:", err);
    } finally {
      setLoading(false);
    }
  };

  const filterAndSortProducts = useCallback(() => {
    if (!Array.isArray(products)) return;

    let result = [...products];
    
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter(product => 
        (product.name || '').toLowerCase().includes(term) ||
        (product.description || '').toLowerCase().includes(term)
      );
    }
    
    if (selectedCategory !== 'all') {
      result = result.filter(product => {
        const catName = product.category_info?.name || product.category;
        return catName === selectedCategory;
      });
    }
    
    if (sortBy === 'price-asc') result.sort((a, b) => a.price - b.price);
    if (sortBy === 'price-desc') result.sort((a, b) => b.price - a.price);

    setFilteredProducts(result);
  }, [searchTerm, selectedCategory, sortBy, products]);

  useEffect(() => { fetchProducts(); }, []);
  useEffect(() => { filterAndSortProducts(); }, [filterAndSortProducts]);

  if (loading) return (
    <div className="flex justify-center items-center min-h-[400px]">
      <div className="w-10 h-10 border-4 border-slate-200 border-t-blue-600 rounded-full animate-spin"></div>
    </div>
  );

  return (
    <div className="space-y-10 animate-fadeIn">
      {/* Filtres */}
      <div className="flex flex-col md:flex-row gap-4 bg-white p-4 rounded-2xl shadow-sm border border-slate-100">
        <div className="relative flex-1">
          <FiSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            type="text"
            placeholder="Rechercher..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-12 pr-4 py-3 bg-slate-50 rounded-xl focus:ring-2 focus:ring-blue-500 transition-all"
          />
        </div>
        <div className="flex gap-2">
          <select
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
            className="px-4 py-3 bg-slate-50 rounded-xl text-sm font-medium border-none"
          >
            {categories.map(c => <option key={c} value={c}>{c === 'all' ? 'Toutes catégories' : c}</option>)}
          </select>
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="px-4 py-3 bg-slate-50 rounded-xl text-sm font-medium border-none"
          >
            <option value="default">Trier par</option>
            <option value="price-asc">Prix croissant</option>
            <option value="price-desc">Prix décroissant</option>
          </select>
        </div>
      </div>

      {/* Grille avec support Vidéo */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-8">
        {filteredProducts.map(product => (
          <ProductCard 
            key={product.id} 
            product={{
              ...product,
              // On s'assure que video_url et image_url sont bien transmis
              image_url: product.image_url || product.image,
              video_url: product.video_url
            }} 
            onAddToCart={onAddToCart} 
          />
        ))}
      </div>

      {/* Pagination fonctionnelle */}
      {(nextPage || prevPage) && (
        <div className="flex justify-center items-center gap-6 pt-10">
            <button 
                onClick={() => handlePageChange(prevPage)}
                disabled={!prevPage}
                className="flex items-center gap-2 px-5 py-2.5 bg-white border border-slate-200 rounded-xl disabled:opacity-30 hover:bg-slate-50 transition-all font-medium"
            >
                <FiChevronLeft /> Précédent
            </button>
            
            <button 
                onClick={() => handlePageChange(nextPage)}
                disabled={!nextPage}
                className="flex items-center gap-2 px-5 py-2.5 bg-slate-900 text-white rounded-xl disabled:opacity-30 hover:bg-blue-600 transition-all font-medium shadow-lg shadow-slate-200"
            >
                Suivant <FiChevronRight />
            </button>
        </div>
      )}
    </div>
  );
}

export default ProductList;